import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import PatientNav from './PatientNav';
import withRoleGuard from '../hoc/withRoleGuard';
import { 
  FaClipboardList, FaClock, FaCheckCircle, FaTimesCircle, FaUser, 
  FaCalendarAlt, FaSignature, FaSearch, FaUserMd, FaComment, 
  FaFilter, FaHourglassHalf, FaEye
} from 'react-icons/fa';

const SubmittedReviews = () => {
  const { nationalId } = useParams();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');

  const procedureTypes = ['All', 'Operative', 'Fixed Prosthodontics', 'Removable Prosthodontics', 'Endodontics', 'Periodontics', 'Oral Surgery'];

  useEffect(() => {
    fetchPatientData();
    fetchReviews();
  }, [nationalId]);

  const fetchPatientData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/patients/public/${nationalId}`);
      setPatientData(response.data);
    } catch (err) {
      console.error('Error fetching patient data:', err);
    }
  };

  const fetchReviews = async () => {
    try {
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reviews/patient/${nationalId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReviews(response.data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.response?.data?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  // Handle different date formats from Firestore
  const formatDate = (dateValue) => {
    try {
      let date;
      
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        return null;
      }
      
      if (isNaN(date.getTime())) return null;
      return date;
    } catch {
      return null;
    }
  };

  const formatDateString = (dateValue) => {
    const date = formatDate(dateValue);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter reviews
  const getFilteredReviews = () => {
    let filtered = reviews;

    if (activeTab === 'pending') {
      filtered = filtered.filter(r => r.status === 'pending');
    } else if (activeTab === 'done') {
      filtered = filtered.filter(r => r.status === 'accepted' || r.status === 'declined' || r.status === 'rejected' || r.status === 'denied');
    }

    if (selectedType !== 'All') {
      filtered = filtered.filter(r => r.procedureType === selectedType);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.procedureType?.toLowerCase().includes(term) ||
        r.supervisorId?.name?.toLowerCase().includes(term) ||
        r.supervisorName?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredReviews = getFilteredReviews();
  const pendingCount = reviews.filter(r => r.status === 'pending').length;
  const doneCount = reviews.filter(r => r.status === 'accepted' || r.status === 'declined' || r.status === 'rejected' || r.status === 'denied').length;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pending</span>;
      case 'accepted':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Accepted</span>;
      case 'declined':
      case 'rejected':
      case 'denied':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Declined</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  // Get step feedback summary
  const getStepFeedback = (review) => {
    if (!review.reviewSteps || review.reviewSteps.length === 0) {
      return { text: 'No steps', color: 'gray' };
    }
    
    const completedSteps = review.reviewSteps.filter(step => step.completed);
    if (completedSteps.length === 0) {
      return { text: 'No completed steps', color: 'gray' };
    }
    
    const acceptedSteps = completedSteps.filter((step, index) => 
      review.stepStatuses?.[index] === 'accepted' || step.supervisorStatus === 'accepted'
    ).length;
    const declinedSteps = completedSteps.filter((step, index) => 
      review.stepStatuses?.[index] === 'declined' || review.stepStatuses?.[index] === 'rejected' ||
      step.supervisorStatus === 'declined' || step.supervisorStatus === 'rejected'
    ).length;
    
    if (acceptedSteps === completedSteps.length) {
      return { text: 'All Accepted', color: 'green', icon: FaCheckCircle };
    } else if (declinedSteps === completedSteps.length) {
      return { text: 'All Declined', color: 'red', icon: FaTimesCircle };
    } else if (acceptedSteps > 0 || declinedSteps > 0) {
      return { text: `${acceptedSteps}/${completedSteps.length} Accepted`, color: 'yellow', icon: FaHourglassHalf };
    }
    return { text: 'Pending Review', color: 'gray', icon: FaHourglassHalf };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <PatientNav />
          <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0077B6]"></div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg"
              >
                <p className="text-red-700">{error}</p>
              </motion.div>
            )}

            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0077B6]">Submitted Reviews</h1>
              <p className="text-gray-600 mt-1">
                View all submitted reviews for {patientData?.fullName || 'this patient'}
              </p>
            </motion.div>

            {/* Main Content Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'all'
                      ? 'text-[#0077B6] border-b-2 border-[#0077B6] bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All ({reviews.length})
                </button>
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'pending'
                      ? 'text-[#0077B6] border-b-2 border-[#0077B6] bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaClock className="h-3 w-3" /> Pending ({pendingCount})
                </button>
                <button
                  onClick={() => setActiveTab('done')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'done'
                      ? 'text-[#0077B6] border-b-2 border-[#0077B6] bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaCheckCircle className="h-3 w-3" /> Done ({doneCount})
                </button>
              </div>

              {/* Filters */}
              <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search by procedure or supervisor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <FaFilter className="text-gray-400 h-4 w-4" />
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm"
                  >
                    {procedureTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reviews List */}
              <div className="p-4">
                {filteredReviews.length === 0 ? (
                  <div className="text-center py-12">
                    <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-gray-500">No reviews found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Procedure</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supervisor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step Feedback</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredReviews.map((review) => {
                          const feedback = getStepFeedback(review);
                          return (
                            <tr key={review._id || review.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedReview(review)}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="font-medium text-gray-900">{review.procedureType || 'N/A'}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {review.supervisorId?.name || review.supervisorName || 'Not assigned'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {getStatusBadge(review.status)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${feedback.color}-100 text-${feedback.color}-600`}>
                                  {feedback.icon && <feedback.icon className="h-3 w-3 mr-1" />}
                                  {feedback.text}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedReview(review); }}
                                  className="text-[#0077B6] hover:text-[#005a8b] text-sm font-medium flex items-center"
                                >
                                  <FaEye className="mr-1 h-4 w-4" /> View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* Review Detail Modal - Same style as Reviews.jsx */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0077B6]">Review Details</h2>
                <button onClick={() => setSelectedReview(null)} className="text-gray-400 hover:text-[#0077B6] transition-all duration-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Patient Information */}
                <div className="bg-[rgba(0,119,182,0.05)] p-6 rounded-lg border border-[rgba(0,119,182,0.1)]">
                  <h3 className="text-lg font-semibold text-[#0077B6] mb-4 flex items-center">
                    <FaUser className="h-5 w-5 mr-2" />
                    Patient Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Name</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedReview.patientId?.fullName || patientData?.fullName || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">National ID</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedReview.patientId?.nationalId || nationalId}</p>
                    </div>
                  </div>
                </div>

                {/* Review Details */}
                <div className="bg-[rgba(0,119,182,0.05)] p-6 rounded-lg border border-[rgba(0,119,182,0.1)]">
                  <h3 className="text-lg font-semibold text-[#0077B6] mb-4 flex items-center">
                    <FaCalendarAlt className="h-5 w-5 mr-2" />
                    Review Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Procedure Type</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedReview.procedureType || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Status</h4>
                      <div className="mt-1">{getStatusBadge(selectedReview.status)}</div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Submission Date</h4>
                      <p className="text-sm text-gray-900 mt-1">{formatDateString(selectedReview.submittedDate || selectedReview.createdAt)}</p>
                    </div>
                    {selectedReview.reviewedDate && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Reviewed Date</h4>
                        <p className="text-sm text-gray-900 mt-1">{formatDateString(selectedReview.reviewedDate)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Supervisor Information */}
                <div className="bg-[rgba(32,178,170,0.05)] p-6 rounded-lg border border-[rgba(32,178,170,0.1)]">
                  <h3 className="text-lg font-semibold text-[#20B2AA] mb-4 flex items-center">
                    <FaUserMd className="h-5 w-5 mr-2" />
                    Supervisor Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Name</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedReview.supervisorId?.name || selectedReview.supervisorName || 'Not assigned'}</p>
                    </div>
                    {selectedReview.supervisorId?.email && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Email</h4>
                        <p className="text-sm text-gray-900 mt-1">{selectedReview.supervisorId.email}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Review Steps */}
                {selectedReview.reviewSteps && selectedReview.reviewSteps.length > 0 && (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Review Steps</h3>
                    
                    {/* Step Summary */}
                    <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Step-by-Step Feedback Summary</h5>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {(() => {
                          const totalSteps = selectedReview.reviewSteps.length;
                          const completedSteps = selectedReview.reviewSteps.filter(step => 
                            typeof step === 'string' ? true : step.completed
                          );
                          const acceptedSteps = completedSteps.filter((step, index) => 
                            selectedReview.stepStatuses?.[index] === 'accepted' || 
                            (typeof step === 'object' && step.supervisorStatus === 'accepted')
                          ).length;
                          const declinedSteps = completedSteps.filter((step, index) => 
                            selectedReview.stepStatuses?.[index] === 'declined' || 
                            selectedReview.stepStatuses?.[index] === 'rejected' ||
                            (typeof step === 'object' && (step.supervisorStatus === 'declined' || step.supervisorStatus === 'rejected'))
                          ).length;
                          
                          return (
                            <>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                Total: {totalSteps} steps
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex items-center">
                                <FaCheckCircle className="mr-1 h-3 w-3" /> Accepted: {acceptedSteps}
                              </span>
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded flex items-center">
                                <FaTimesCircle className="mr-1 h-3 w-3" /> Declined: {declinedSteps}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Individual Steps */}
                    <div className="space-y-2">
                      {selectedReview.reviewSteps.map((step, index) => {
                        const stepStatus = selectedReview.stepStatuses?.[index] || (typeof step === 'object' ? step.supervisorStatus : null);
                        const isAccepted = stepStatus === 'accepted';
                        const isDeclined = stepStatus === 'declined' || stepStatus === 'rejected';
                        const isCompleted = typeof step === 'object' ? step.completed : true;
                        
                        // Get step name - handle both string and object formats
                        const stepName = typeof step === 'string' 
                          ? step 
                          : (step.name || step.step || step.stepName || step.description || step.title || `Step ${index + 1}`);
                        
                        return (
                          <div 
                            key={index} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isAccepted ? 'bg-green-50 border-green-200' : 
                              isDeclined ? 'bg-red-50 border-red-200' : 
                              isCompleted ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium mr-3 ${
                                isCompleted ? 'bg-[#0077B6] text-white' : 'bg-gray-300 text-gray-600'
                              }`}>
                                {index + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                {stepName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {isAccepted && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  <FaCheckCircle className="mr-1 h-3 w-3" /> Accepted
                                </span>
                              )}
                              {isDeclined && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <FaTimesCircle className="mr-1 h-3 w-3" /> Declined
                                </span>
                              )}
                              {!isAccepted && !isDeclined && isCompleted && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  <FaHourglassHalf className="mr-1 h-3 w-3" /> Pending
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Comments */}
                {(selectedReview.comment || selectedReview.supervisorComment) && (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaComment className="h-5 w-5 mr-2 text-[#0077B6]" />
                      Comments
                    </h3>
                    <div className="space-y-4">
                      {selectedReview.comment && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1">Student Comment</h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border">{selectedReview.comment}</p>
                        </div>
                      )}
                      {selectedReview.supervisorComment && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1">Supervisor Comment</h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border">{selectedReview.supervisorComment}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Signatures */}
                {(selectedReview.studentSignature || selectedReview.supervisorSignature) && (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaSignature className="h-5 w-5 mr-2 text-[#0077B6]" />
                      Signatures
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {selectedReview.studentSignature && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-2">Student Signature</h4>
                          <div className="bg-white p-3 rounded border">
                            <img src={selectedReview.studentSignature} alt="Student Signature" className="max-h-20 mx-auto" />
                          </div>
                        </div>
                      )}
                      {selectedReview.supervisorSignature && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-2">Supervisor Signature</h4>
                          <div className="bg-white p-3 rounded border">
                            <img src={selectedReview.supervisorSignature} alt="Supervisor Signature" className="max-h-20 mx-auto" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedReview(null)}
                  className="px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default withRoleGuard(['student'])(SubmittedReviews);
